import { statusMeta } from "./format";
import { isResidential } from "./propertyKind";

/**
 * Nearby launches for a project page: the same district first, then the
 * two neighbouring district numbers when fewer than four are found, never a
 * sold-out or completed project, ordered by distance where both records
 * carry coordinates, capped at six. Shared by the server route and the
 * client-side top-up so both lists agree.
 */
export type NearbyCandidate = {
  _id: string;
  name: string;
  title?: string | null;
  property_type?: string | null;
  district?: string | null;
  construction_status?: string | null;
  isPreLaunch?: boolean;
  location?: { latitude?: number | null; longitude?: number | null } | null;
};

export const NEARBY_CAP = 6;
const MIN_BEFORE_WIDENING = 4;

export function districtNumber(code: string | null | undefined): number | null {
  const m = String(code ?? "").match(/(\d{1,2})/);
  return m ? Number(m[1]) : null;
}

export function coordsOf(p: { location?: { latitude?: number | null; longitude?: number | null } | null }) {
  const lat = Number(p.location?.latitude);
  const lng = Number(p.location?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 ? { lat, lng } : null;
}

export function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number) {
  const r = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

export function isOpenForSale(p: Pick<NearbyCandidate, "construction_status" | "isPreLaunch">) {
  const tone = statusMeta(p.construction_status || "Upcoming", p.isPreLaunch).tone;
  return tone !== "soldout" && tone !== "completed";
}

export function selectNearby<T extends NearbyCandidate>(current: NearbyCandidate, candidates: T[]): T[] {
  const here = districtNumber(current.district);
  const origin = coordsOf(current);
  const seen = new Set<string>([current._id]);
  const eligible: T[] = [];
  for (const p of candidates) {
    if (!p?._id || seen.has(p._id) || p.name === current.name) continue;
    seen.add(p._id);
    if (!isOpenForSale(p)) continue;
    // A food factory or an industrial unit is not a shortlist candidate for
    // someone reading a home page, however close it sits.
    if (!isResidential(p)) continue;
    eligible.push(p);
  }

  const byDistance = (a: T, b: T) => {
    const ca = coordsOf(a);
    const cb = coordsOf(b);
    const da = origin && ca ? distanceKm(origin.lat, origin.lng, ca.lat, ca.lng) : Infinity;
    const db = origin && cb ? distanceKm(origin.lat, origin.lng, cb.lat, cb.lng) : Infinity;
    return da - db || a.name.localeCompare(b.name);
  };

  if (here == null) return eligible.sort(byDistance).slice(0, NEARBY_CAP);

  const same = eligible.filter((p) => districtNumber(p.district) === here).sort(byDistance);
  if (same.length >= MIN_BEFORE_WIDENING) return same.slice(0, NEARBY_CAP);
  const adjacent = eligible
    .filter((p) => {
      const d = districtNumber(p.district);
      return d === here - 1 || d === here + 1;
    })
    .sort(byDistance);
  return [...same, ...adjacent].slice(0, NEARBY_CAP);
}
